/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { ImageIcon } from '@/shared/ui/radix-icons';
import type { CatalogPublication } from '../types';
import { Image, Text, UnstyledButton } from '@/shared/ui';
import { formatLocalizedDate } from '@/shared/utils/date';
import { LeadAdBadge } from './LeadAdBadge';

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  published: { bg: 'var(--seed-base)', text: '#fff' },
  queued: { bg: 'var(--status-warning)', text: 'var(--status-warning-text)' },
  scheduled: { bg: 'var(--status-warning)', text: 'var(--status-warning-text)' },
  error: { bg: 'var(--status-error)', text: 'var(--status-error-text)' },
};

/**
 * Resolves badge colors for a publication status.
 *
 * @param status - Backend status value, possibly null.
 * @returns Background and text color tokens for the status badge.
 */
function statusStyle(status: string | null) {
  const s = status ?? '';
  return STATUS_COLORS[s] ?? { bg: 'var(--surface-tertiary)', text: 'var(--text-secondary)' };
}

const STATUS_LABEL_KEYS: Record<string, string> = {
  published: 'publications.status.published',
  queued: 'publications.status.queued',
  scheduled: 'publications.status.scheduled',
  error: 'publications.status.error',
};

/**
 * Resolves the localized label for a publication status.
 *
 * @param status - Backend status value, possibly null.
 * @param t - Translation function scoped to the properties namespace.
 * @returns Localized status label, or the raw status when unmapped.
 */
function statusLabel(status: string | null, t: (key: string) => string): string {
  const key = status ? STATUS_LABEL_KEYS[status] : undefined;
  return key ? t(key) : (status ?? '?');
}

interface Props {
  entry: CatalogPublication;
  onSelect: (entry: CatalogPublication) => void;
}

/**
 * Renders a single publication card with its status, image, and metadata.
 *
 * @param props - The publication entry and selection handler.
 * @returns The publication card element.
 */
export function PublicationCard({ entry, onSelect }: Props) {
  const { t } = useTranslation('@features/properties');
  const { bg, text } = statusStyle(entry.status);
  const dateLabel = entry.published_at
    ? formatLocalizedDate(entry.published_at)
    : null;

  const thumbnailSrc = entry.property_image;

  return (
    <UnstyledButton
      type="button"
      data-testid="publication-card"
      onClick={() => onSelect(entry)}
      className="group relative flex flex-col rounded-(--radius-xl) overflow-hidden cursor-pointer border-none p-0 text-left snap-start focus-visible:outline-2 focus-visible:outline-offset-2 outline-none"
      style={{
        width: 'clamp(150px, calc(44vw - 20px), 240px)',
        flexShrink: 0,
        background: 'var(--surface-primary)',
        boxShadow: 'var(--shadow-sm)',
        transition: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 200ms',
        borderRadius: 'var(--radius-xl)',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.transform = 'scale(1.04)';
        (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-lg)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
        (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-sm)';
      }}
    >
      {/* Thumbnail */}
      <div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: '4 / 3', background: 'var(--surface-secondary)' }}
      >
        {thumbnailSrc ? (
          <Image
            src={thumbnailSrc}
            alt={entry.property_title ?? ''}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div
            data-testid="publication-card-placeholder"
            className="w-full h-full flex items-center justify-center"
            style={{ background: 'var(--surface-tertiary)' }}
            aria-hidden="true"
          >
            <ImageIcon
              width={28}
              height={28}
              style={{ color: 'var(--text-tertiary)' }}
            />
          </div>
        )}

        {/* Lead Ad running on this post */}
        <div className="absolute bottom-2 left-2">
          <LeadAdBadge entry={entry} />
        </div>

        {/* Status badge overlay */}
        <Text component="span"
          data-testid="publication-status-badge"
          className="absolute top-2 right-2 px-(--space-2) py-(--space-0-5) rounded-full font-semibold leading-none"
          style={{ background: bg, color: text, fontSize: 10 }}
        >
          {statusLabel(entry.status, t)}
        </Text>
      </div>

      {/* Info */}
      <div className="px-(--space-3) py-(--space-2-5) flex flex-col gap-(--space-1)">
        <Text
          className="m-0 font-semibold leading-snug line-clamp-2"
          style={{ color: 'var(--text-primary)', fontSize: 'clamp(11px, 1.2vw, 13px)', minHeight: 30 }}
        >
          {entry.property_title ?? t('publications.catalog.noTitle')}
        </Text>
        {dateLabel && (
          <Text
            data-testid="publication-date"
            className="m-0"
            style={{ fontSize: 10, color: 'var(--text-tertiary)' }}
          >
            {dateLabel}
          </Text>
        )}
      </div>
    </UnstyledButton>
  );
}
